/**
 * A content timestamp in the form schema.org wants, or null when there is no usable date.
 *
 * Content reaches a page without dates more often than one might expect — a game with no
 * schedule entered, a video that has never been published, anything being previewed
 * before it exists publicly — and `new Date(undefined).toISOString()` throws
 * `RangeError: Invalid time value`. Thrown from a JSON-LD block, that costs the whole
 * page: on the client it breaks the render, and in the SSR shell it fails the response.
 *
 * So this returns null instead, and callers leave the property out. Structured data
 * missing a property costs a crawler warning; an exception costs the page.
 *
 * Seconds and milliseconds are both accepted because the content API sends both,
 * depending on the field.
 */
export const toSchemaDate = (value: unknown): string | null => {
  const numeric = Number(value);
  // Nothing at or before the epoch is a date content can carry, and zero is what a
  // missing value becomes once `Number` has had its way with null, '' and false.
  if (!Number.isFinite(numeric) || numeric <= 0) return null;

  // Anything below this is too small to be milliseconds — it would be 1970 — so it is
  // seconds. The same test the SSR shell has always used.
  const milliseconds = numeric < 1e10 ? numeric * 1000 : numeric;

  // Finite is not enough: a timestamp beyond the range a Date can hold is an Invalid
  // Date, and asking one for an ISO string is what throws.
  const date = new Date(milliseconds);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};
